import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, Storage } from "aws-amplify";
import { listPortfolioImages } from "../graphql/queries";
import ImageBundle from "./ImageBundle";
import "./Portfolio.css";

const categories = [
  { id: "all", name: "ALL PROJECTS" },
  { id: "pavers", name: "PAVERS" },
  { id: "concrete", name: "CONCRETE" },
  { id: "steps", name: "STEPS" },
  { id: "retaining", name: "RETAINING WALLS" },
];

export default function Portfolio() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [dynamicImages, setDynamicImages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDynamicImages();
  }, []);

  const fetchDynamicImages = async () => {
    setLoading(true);
    setError(null);
    try {
      // Paginate through all results
      let allItems = [];
      let nextToken = null;
      do {
        const result = await API.graphql({
          query: listPortfolioImages,
          variables: { limit: 500, nextToken },
        });
        allItems = allItems.concat(result.data.listPortfolioImages.items);
        nextToken = result.data.listPortfolioImages.nextToken;
      } while (nextToken);

      // Resolve S3 URLs and group by category
      const grouped = { pavers: [], concrete: [], steps: [], retaining: [] };
      await Promise.all(
        allItems.map(async (img) => {
          try {
            const url = await Storage.get(img.s3Key);
            if (grouped[img.category]) {
              grouped[img.category].push({ url, order: img.order });
            }
          } catch (err) {
            console.error("Error resolving S3 URL:", err);
          }
        })
      );

      // Sort each category by order
      Object.keys(grouped).forEach((cat) => {
        grouped[cat].sort((a, b) => a.order - b.order);
      });

      setDynamicImages(grouped);
    } catch (err) {
      console.error("Error fetching portfolio images:", err);
      setError("Failed to load portfolio images.");
    } finally {
      setLoading(false);
    }
  };

  const getDataForCategory = (cat) => {
    if (dynamicImages && dynamicImages[cat]) {
      return dynamicImages[cat];
    }
    return [];
  };

  const getData = () => {
    switch (activeCategory) {
      case "pavers": return getDataForCategory("pavers");
      case "concrete": return getDataForCategory("concrete");
      case "steps": return getDataForCategory("steps");
      case "retaining": return getDataForCategory("retaining");
      default: return [
        ...getDataForCategory("pavers").slice(0, 5),
        ...getDataForCategory("concrete").slice(0, 4),
        ...getDataForCategory("steps").slice(0, 5),
        ...getDataForCategory("retaining").slice(0, 4),
      ];
    }
  };

  const renderGalleryContent = () => {
    if (loading) {
      return (
        <div className="portfolio-loading">
          <div className="portfolio-spinner"></div>
          <p>Loading portfolio...</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="portfolio-error">
          <p>{error}</p>
          <button className="portfolio-retry-btn" onClick={fetchDynamicImages}>
            Try Again
          </button>
        </div>
      );
    }
    const data = getData();
    if (data.length === 0) {
      return (
        <div className="portfolio-empty">
          <p>No images available for this category.</p>
        </div>
      );
    }
    return <ImageBundle data={data} />;
  };

  return (
    <div className="portfolio-page">
      {/* Hero */}
      <section className="portfolio-hero">
        <div className="portfolio-hero-bg"></div>
        <div className="portfolio-hero-content">
          <span className="hero-label">OUR WORK</span>
          <h1 className="hero-title">
            PROJECT <span className="gold-text">PORTFOLIO</span>
          </h1>
          <p className="hero-description">
            Browse our collection of completed projects across Central New Jersey.
            Every image represents our commitment to excellence.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="portfolio-stats">
        <div className="portfolio-container">
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-number">2,500+</span>
              <span className="stat-label">PROJECTS COMPLETED</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">40+</span>
              <span className="stat-label">YEARS OF EXPERIENCE</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">100%</span>
              <span className="stat-label">SATISFACTION RATE</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="portfolio-filter">
        <div className="portfolio-container">
          <div className="filter-tabs">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`filter-tab ${activeCategory === cat.id ? "active" : ""}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="portfolio-gallery">
        <div className="portfolio-container">
          {renderGalleryContent()}
        </div>
      </section>

      {/* CTA */}
      <section className="portfolio-cta">
        <div className="portfolio-container">
          <div className="cta-content">
            <h2>LIKE WHAT YOU SEE?</h2>
            <p>Let's discuss how we can transform your property</p>
            <Link to="/estimate" className="btn-primary">
              GET FREE ESTIMATE
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
